"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { useEffect, useState } from "react";
import { PARENT_COMPANY, SITE } from "@/lib/content";

const LINKS = [
  { href: "/", label: "Home" },
  { href: "/about", label: "About" },
  { href: "/companies", label: "Companies" },
  { href: "/blogs", label: "Blogs" },
  { href: "/contact", label: "Contact" },
];

function isDark(): boolean {
  const a = document.documentElement.getAttribute("data-theme");
  if (a) return a === "dark";
  return window.matchMedia("(prefers-color-scheme: dark)").matches;
}

export function SiteNav() {
  const pathname = usePathname();
  const [open, setOpen] = useState(false);
  const [dark, setDark] = useState(false);

  useEffect(() => {
    try {
      const saved = localStorage.getItem("jdlweb.theme");
      if (saved) document.documentElement.setAttribute("data-theme", saved);
    } catch {
      /* ignore */
    }
    setDark(isDark());
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  useEffect(() => {
    function closeMenu() {
      setOpen(false);
    }
    window.addEventListener("jd:nav-close", closeMenu);
    return () => window.removeEventListener("jd:nav-close", closeMenu);
  }, []);

  function toggleTheme() {
    const next = isDark() ? "light" : "dark";
    document.documentElement.setAttribute("data-theme", next);
    try {
      localStorage.setItem("jdlweb.theme", next);
    } catch {
      /* ignore */
    }
    setDark(next === "dark");
  }

  function isActive(href: string): boolean {
    if (href === "/") return pathname === "/";
    return pathname.startsWith(href);
  }

  return (
    <header className={"site-nav" + (open ? " is-open" : "")}>
      <Link href="/" className="nav-brand brand" onClick={() => setOpen(false)}>
        <img
          className="nav-logo"
          src="/logo-64.webp"
          alt=""
          width={48}
          height={48}
          decoding="async"
        />
        <span className="brand-copy">
          <strong>{SITE.short.toUpperCase()}</strong>
          <span className="brand-tagline">{PARENT_COMPANY.tagline}</span>
        </span>
      </Link>
      <nav className="nav-links" aria-label="Primary">
        {LINKS.map((l) => (
          <Link
            key={l.href}
            href={l.href}
            className={isActive(l.href) ? "is-active" : undefined}
            onClick={() => setOpen(false)}
          >
            {l.label}
          </Link>
        ))}
      </nav>
      <div className="nav-cta">
        <button className="theme-btn" type="button" aria-label="Toggle theme" onClick={toggleTheme}>
          {dark ? "☀" : "☾"}
        </button>
        <Link href="/contact" className="btn btn-primary" onClick={() => setOpen(false)}>
          Get in touch
        </Link>
      </div>
      <button
        className="nav-toggle icon-round"
        type="button"
        aria-label="Menu"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        ☰
      </button>
    </header>
  );
}
